import type { Translations } from './types'

export const en: Translations = {
  common: {
    stationUnknown: (nr) => `Station ${nr} is not active yet.`,
    stationOffline: 'No connection — please scan again later.',
    toMap: 'To the map',
    more: 'More',
    images: 'Images',
    retry: 'Try again',
    back: 'Back',
    close: 'Close',
    processing: 'Signing in…',
  },

  landing: {
    hero: {
      title: 'Experience Nature in\nAugmented Reality',
      subtitle: () =>
        `Coming soon — sign up now to be the first to know when TSCHAPP goes live.`,
      cta: 'Sign Up Now',
      secondaryCta: 'Explore',
    },
    intro: {
      heading: 'The Gorge\nComes Alive',
      text: (count) =>
        `TSCHAPP brings the Tscheppaschlucht in Ferlach to life. With Augmented Reality you can explore over ${count} native animal and plant species directly in their natural habitat \u2014 with detailed descriptions, AI-generated knowledge articles and immersive 3D models.`,
    },
    stats: {
      animals: 'Animal Species',
      plants: 'Plant Species',
      ar: 'Augmented Reality',
    },
    community: {
      heading: 'Join the Community',
      text: 'With an account you can like species, leave comments and experience the Tscheppaschlucht with other nature enthusiasts.',
      features: {
        like: {
          title: 'Like Species',
          description:
            'Mark your favourite species and create your personal collection',
        },
        comment: {
          title: 'Comment',
          description:
            'Share your knowledge and connect with other nature lovers',
        },
        ar: {
          title: 'Experience AR',
          description:
            'Experience species in Augmented Reality right in the gorge',
        },
      },
      registerWithGoogle: 'Sign up with Google',
    },
    routes: {
      heading: 'Three Paths, One Experience',
      text: 'All routes start at the visitor centre and lead through the Tscheppaschlucht. At the fork you choose your adventure.',
      stations: 'Stations',
      detailLink: 'All Routes in Detail',
    },
    stories: {
      heading: 'Stories from the Gorge',
      text: 'Legends, myths and the stories of our mascots \u2014 told as an interactive audiobook with AI illustrations.',
      narratorTschauko: 'Narrated by Tschauko',
      narratorTschauka: 'Narrated by Tschauka',
      scenes: 'Scenes',
      allLink: 'All Stories',
    },
    featured: {
      heading: 'Featured Species',
      discoverLink: (count) => `Discover All ${count} Species`,
    },
  },

  browse: {
    badge: 'Flora & Fauna',
    heroTitle: 'Flora & Fauna',
    heroSubtitle: 'Discover the wildlife of the Tscheppaschlucht',
    speciesCount: (count) => `${count} Species`,
    filterAll: 'All',
    logout: 'Sign Out',
    login: 'Sign In',
    authBanner: 'Sign in to like species and leave comments',
    close: 'Close',
  },

  species: {
    loading: 'Loading object...',
    analyzing: 'Analysing object...',
    loading3d: 'Loading 3D model...',
    preparing3d: 'Preparing 3D model …',
    load3dFailed: 'The 3D model could not be loaded.',
    arStartFailed: 'AR could not be started. Please check the browser’s WebXR permission.',
    notFound: 'Object not found',
    analysisFailed: 'Analysis failed',
    noId: 'No ID provided',
    brandSubtitle: 'Experience Nature in Augmented Reality',
    featuresCount: (count) => `${count} Features`,
    share: 'Share',
    like: 'Like',
    unlike: 'Unlike',
    comments: 'Comments',
    audioGuide: {
      label: 'Audio Guide',
      subtitle: 'Listen to description',
      noAudio: 'No audio',
    },
    pause: 'Pause',
    play: 'Play',
    stopMusic: 'Stop music',
    view2d: '2D View',
    view3d: '3D View',
    commentsSection: {
      title: (count) => `Comments (${count})`,
      empty: 'No comments yet. Be the first!',
      placeholder: 'Write a comment...',
      delete: 'Delete',
    },
    close: 'Close',
    back: 'Back',
  },

  routes: {
    headerTitle: 'Hiking Trails',
    heroTitle: 'Three Paths Through\nthe Gorge',
    heroSubtitle:
      'All routes start at the visitor centre and lead through the impressive Tscheppaschlucht. At the fork you decide which adventure awaits you.',
    stats: {
      kilometers: 'Kilometres',
      walkingTime: 'Walking Time',
      stations: 'Stations',
    },
    sections: {
      routeCourse: 'Route Overview',
      elevationProfile: 'Elevation Profile',
      stations: 'Stations',
    },
    listToggle: 'List',
    loading3d: 'Loading 3D view\u2026',
    elevationMin: 'min',
    elevationMax: 'max',
    back: 'Back',
    details: {
      listenDescription: 'Listen to description',
      stopListening: 'Stop',
      listenRoute: 'Listen to route',
      guideLive: 'Dr. Tschauko live',
      nextStations: 'Next stations',
    },
  },

  stories: {
    languageFallback: 'This legend is currently only available in German.',
    badge: 'Legends & Stories',
    pageTitle: 'Legends & Stories',
    pageSubtitle:
      'The myths and legends of the Tscheppaschlucht \u2014 narrated by Tschauko & Tschauka',
    narratorTschauko: '\uD83E\uDD89 Narrated by Tschauko',
    narratorTschauka: '\uD83D\uDC38 Narrated by Tschauka',
    chapterCounter: (current, total) => `Chapter ${current} of ${total}`,
    chapterCount: (count) => `${count} Chapters`,
    startAudiobook: 'Start Audiobook',
    closeAudiobook: 'Close Audiobook',
    previousChapter: 'Previous Chapter',
    nextChapter: 'Next Chapter',
    done: 'Done',
    chapterN: (n) => `Chapter ${n}`,
    toggleMode: 'Switch mode',
    photoBackground: 'Photo background',
    back: 'Back',
  },

  footer: {
    tagline:
      'Experience Nature in Augmented Reality.',
    legal: {
      title: 'Legal',
      imprint: 'Imprint',
      privacy: 'Privacy Policy',
      terms: 'Terms of Use',
    },
    contact: 'Contact',
    more: {
      title: 'More',
      floraFauna: 'Flora & Fauna',
    },
    sponsoredBy: 'Supported by',
    copyright: (year) => `\u00A9 ${year} TSCHAPP \u00B7 All rights reserved`,
  },

  units: {
    minuteShort: 'min',
    hourShort: 'hr',
  },

  mapMarkers: {
    ticketOffice: 'Ticket office',
    receptionHouse: 'Visitor centre',
    goal: 'Finish',
  },

  legend: {
    title: 'Legend',
    mainRoute: 'Main route',
    sideTrail: 'Side trail',
    waterways: 'Waterways',
    filterOnly: (kategorie: string) => `Show only ${kategorie}`,
    filterClear: 'Clear filter — show all',
    byDistance: 'Distance',
    byName: 'Name',
  },

  nav: {
    home: 'Home',
    floraFauna: 'Flora & Fauna',
    routes: 'Hiking Trails',
    stories: 'Stories',
    login: 'Sign In',
    logout: 'Sign Out',
    myProfile: 'My Profile',
  },

  dialog: {
    register: {
      title: 'Register',
      description:
        'Join the Tscheppaschlucht Community. Discover nature in Augmented Reality.',
      nameLabel: 'Name',
      namePlaceholder: 'Your name',
      emailLabel: 'Email',
      emailPlaceholder: 'name@example.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Min. 8 characters',
      submit: 'Create Account',
      google: 'Sign up with Google',
      alreadyRegistered: 'Already registered?',
      loginLink: 'Sign In',
      or: 'or',
      emailSent: 'We\u2019ve sent you a confirmation. You can continue now!',
      emailHint: 'We\u2019ll let you know when TSCHAPP goes live.',
    },
    login: {
      title: 'Sign In',
      description:
        'Like species, leave comments and experience the Tscheppaschlucht with other nature enthusiasts.',
      emailLabel: 'Email',
      emailPlaceholder: 'name@example.com',
      passwordLabel: 'Password',
      passwordPlaceholder: '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022',
      submit: 'Sign In',
      google: 'Sign in with Google',
      noAccount: 'No account yet?',
      registerLink: 'Register',
    },
    survey: {
      title: 'Quick Survey',
      description: 'Help us make TSCHAPP even better — 4 quick questions.',
      interestLabel: 'What interests you the most?',
      interestOptions: {
        ar: 'AR experience in nature',
        flora: 'Discover flora & fauna',
        routes: 'Hiking routes',
        stories: 'Legends & stories',
      },
      visitLabel: 'How often do you visit the Tscheppaschlucht?',
      visitOptions: {
        never: 'Never been',
        once: '1–2 times',
        regular: 'Regularly',
        local: 'I live nearby',
      },
      deviceLabel: 'Which device do you use?',
      deviceOptions: {
        iphone: 'iPhone',
        android: 'Android',
      },
      wishLabel: 'What would you like the app to have?',
      wishPlaceholder: 'Your ideas (optional)',
      submit: 'Submit',
      skip: 'Skip',
    },
  },

  navigation: {
    button: 'Navigation',
    title: 'Map',
    close: 'Close map',
    centerOnUser: 'Centre on my location',
    nearestPoint: 'Nearest point',
    distance: (meters) => meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`,
    learnMore: 'Learn more',
    playAudio: 'Play audio',
    locationPermissionDenied: 'Location access denied',
    loadingMap: 'Loading map…',
    noGps: 'No GPS signal',

    loadFailed: 'Map could not be loaded',
    aria: {
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      recenter: 'Centre on my location',
      followEnable: 'Follow my location on the map',
      followDisable: 'Stop following my location',
    },
    aux: {
      storyGuideOpen: 'Open Story Guide',
      storyGuideClose: 'Close Story Guide',
      aiCompanionStart: 'Start AI companion',
      aiCompanionPause: 'Pause AI companion',
      photoMap: 'Show photo map',
      vectorMode: 'Vector mode',
      diaryEnd: 'End diary',
    },
    optionsSheet: {
      adventure: 'Adventure',
      performance: 'Performance',
      map: 'Map',
      narrator: 'Narrator',
      voiceComing: 'Voice coming soon',
    },
    poiCard: {
      audioBtn: 'Audio',
      audioStop: 'Stop',
      moreBtn: 'More →',
      endBtn: 'End',
      metersAway: 'm away',
      fromStart: 'from start',
      away: 'away',
      sightLabel: 'Point of interest',
    },
    // Routen-Karte (Picker) — Auswaehlen/Starten/Details (Alex 25.07.).
    /** Weggabelung: Routenwechsel direkt im Detail des Punktes
     *  (Alex 2026-08-11 — „wenn man da ist, will man vielleicht doch eine
     *  andere Route wandern"). */
    junction: {
      title: 'From here',
      hint: 'These trails branch off. Tap to switch route.',
      active: 'Active route',
    },
    routeCard: {
      select: 'Select',
      active: 'Active',
      start: 'Start route',
      details: 'Details',
    },
  },

  // Entdecken-Oberflaeche. NUR Bedienelemente — POI-Titel, Beschreibungen
  // und Kategorien kommen sprachselektiert aus ArTrack/knowledge-api.
  discover: {
    title: 'Discover',
    close: 'Close',
    back: 'Back',
    searchPlaceholder: 'Search places, stories, animals …',
    searchLabel: 'Search Discover',
    categoryFilterLabel: 'Filter by category',
    detailLabel: 'Place details',
    unavailable: 'This place is currently unavailable.',
    highlights: 'Highlights',
    nearby: 'Nearby',
    allPlaces: 'All places',
    categories: 'Categories',
    filterAll: 'All',
    unitSpecies: 'species',
    unitPlaces: 'places',
    heroSubtitle: 'Stories, nature and secrets of the Tscheppaschlucht gorge.',
    openMap: 'Open map',
    showAll: 'Show all',
    showOnMap: 'Show on map',
    readAloud: 'Read aloud',
    loading: 'Loading content …',
    nothingFound: (suche: string) => suche
      ? `Nothing found for “${suche}”.`
      : `Nothing found in this category.`,
    stopReading: 'Stop reading',
  },

  footerNav: {
    profileSignIn: 'Profile — sign in',
    profile: 'Profile',
    map: 'Map',
    discover: 'Discover',
    chatBot: 'Dr. Tschauko',
    diary: 'Diary',
    debug: 'Debug',
    route: 'Route',
  },

  onboarding: {
    welcome: {
      title: 'Thanks for signing up!',
      text: 'TSCHAPP brings you closer to the nature of the Tscheppaschlucht \u2014 with Augmented Reality, interactive maps and fascinating stories. Here\u2019s a quick overview.',
    },
    flora: {
      title: 'Flora & Fauna',
      text: 'Discover over 80 native animal and plant species with detailed descriptions, 3D models and AI-generated knowledge articles. Like and comment on every species.',
    },
    stories: {
      title: 'Legends & Stories',
      text: 'Experience the myths and legends of the Tscheppaschlucht as an interactive audiobook \u2014 narrated by our mascots Tschauko & Tschauka, illustrated with AI-generated art.',
    },
    routes: {
      title: 'Hiking Routes',
      text: 'Three unique paths lead through the gorge. Each route has its own stations with AR experiences, elevation profiles and interactive 3D views.',
    },
    next: 'Next',
    prev: 'Back',
    skip: 'Skip',
    start: 'Let\u2019s go',
  },

  expedition: {
    title: 'My Expedition',
    progress: (found, total) => `${found} of ${total} secrets discovered`,
    nextSecret: 'Find the next secret',
    nextSecretSub: 'Follow the next clue and explore the gorge.',
    today: 'Discovered today',
    newFound: 'Newly discovered',
    nextHint: 'Next clue',
    myFinds: 'My finds',
    secretSlot: 'Secret',
    emptyRoute: 'No secrets recorded for this trail yet.',
    badges: 'Badges',
    badgeTracker: { title: 'Tracker', text: 'You find clues others walk past.' },
    badgePlants: { title: 'Plant expert', text: 'You know the plants of the gorge.' },
    missionTitle: 'Your mission',
    secretNofM: (n, m) => `Secret ${n} of ${m}`,
    lookLike: 'It could look like this',
    whatYouGet: 'What you get',
    rewardFind: { title: 'New find', sub: 'Discover something special.' },
    rewardProgress: { title: '+1 progress', sub: 'Your expedition keeps growing.' },
    rewardSecret: { title: '1 secret closer', sub: 'One step closer to the solution.' },
    rewardFindDone: 'This find has been added to your collection.',
    rewardSecretDone: 'Secret solved',
    lookout: 'I will keep looking',
    foundIt: 'I found it',
    later: 'Later',
    fundTitle: 'Great discovery',
    solvedNofM: (n, m) => `Secret ${n} of ${m} solved`,
    youFound: (name) => `You found it: ${name}.`,
    progressLabel: 'Expedition progress',
    intoBook: 'Into the discovery book',
    learnMore: 'Learn more',
  },

  forschung: {
    title: 'Research journal',
    subtitle: 'Record and understand your observations.',
    newObservation: '+ New observation',
    statBeobachtungen: 'Observations',
    statArten: 'Species',
    statEntwuerfe: 'Drafts',
    drafts: 'Drafts',
    myEntries: 'My entries',
    emptyEntries: 'No observations yet. Whatever you see, hear or find along the way belongs here.',
    untitled: 'Untitled',
    status: { draft: 'Draft', private: 'Private', submitted: 'Under review', published: 'Published', rejected: 'Not published', queued: 'Waiting for upload', queuedRetry: 'Upload retrying' },
    newTitle: 'New observation',
    gpsPending: 'Location will be added as soon as GPS is available.',
    capFoto: 'Photo',
    capMediathek: 'Library',
    capAudio: 'Audio',
    capAudioStop: (s) => `${s}s stop`,
    capVideo: 'Video',
    mediaHint: (sec) => `Photos are automatically resized for upload (HD). Videos: ${sec} seconds at most.`,
    videoTooLong: (sec, max) => `The video is ${sec} seconds long — the journal allows at most ${max} seconds so uploads succeed even with weak reception.`,
    videoTooBig: (mb, max) => `The video is too large (${mb} MB). Please record at most ${max} seconds in HD.`,
    titleLabel: 'Title',
    noteLabel: 'Note',
    notePlaceholder: 'What did you observe?',
    defaultTitleAt: (ort) => `Observation at ${ort}`,
    defaultTitle: 'Observation',
    artLabel: 'Link a species (optional)',
    artSearch: 'Search species name …',
    artUnlink: 'Remove link',
    save: 'Save entry',
    saveHint: 'Saved privately — visible only to you. Submitting to the community is a separate step afterwards.',
    back: 'Back',
    suggest: 'Suggest for publication',
    previewHeading: 'How your post will appear',
    previewHint: 'Your entry will be reviewed before it appears in the community.',
    submit: 'Submit entry',
    submitting: 'Submitting …',
    keepPrivate: 'Keep private',
    edit: 'Edit',
    delete: 'Delete entry',
    deleteConfirm: 'Delete this entry permanently?',
    keep: 'Keep',
    rejectedInfo: (reason) => `The editors did not publish this post${reason ? `: ${reason}` : '.'} Your private entry remains.`,
    submitFailed: (detail) => `Submission failed — the entry remains saved privately. (${detail})`,
    queuedInfo: (attempts) => `Submitted — the upload will run as soon as there is a connection${attempts ? ` (${attempts} ${attempts === 1 ? 'attempt' : 'attempts'} so far)` : ''}. You can close the app; it will keep trying next time you open it.`,
    backToList: 'Back to overview',
    fallbackOrt: 'Tscheppaschlucht',
    community: 'From the gorge',
    communitySub: 'The community discovers, shares and preserves.',
  },
}
